import { Bag, removeProduct } from "@repo/apis/bag";
import { ExpandedBag } from "../utils/bag.type";
import { cache } from "../utils/cache";
import { buildBagExpansionsForApiInteraction } from "../utils/expansions";
import { handleApiInteractionError } from "../utils/handle-api-interaction-error";
import { handleApiInteractionSuccess } from "../utils/handle-api-interaction-success";
import { BagSdkMethod } from "../utils/method-initialiser";

type PublicRemoveProductMethod = {
  (params: { bagItemId: string; expand: true }): Promise<ExpandedBag>;
  (params: { bagItemId: string; expand?: false }): Promise<Bag>;
};
type RemoveProductMethod = BagSdkMethod<PublicRemoveProductMethod>;

export const removeProductInitialiser: RemoveProductMethod = (config) =>
  (async ({ bagItemId, expand }) => {
    const { chameleonRequestInstance, store, lang, getAuthHeader } = config;

    const authHeader = await getAuthHeader();
    const bagSummary = await cache.getSummary(config);
    const bagId = bagSummary?.bagId;

    if (!bagId) {
      throw new Error("Failed to remove product. The bag does not exist");
    }

    const bagSessionToken = bagSummary?.sessionToken;

    if (!bagSessionToken) {
      throw new Error(
        "Failed to remove product. The bag session token does not exist",
      );
    }

    const bagExpansions = buildBagExpansionsForApiInteraction({
      expand,
    });

    try {
      const removeProductResult = await removeProduct({
        chameleonRequestInstance,
        bagItemId,
        store,
        lang,
        authHeader,
        asosBagSessionToken: bagSessionToken,
        bagId,
        expand: bagExpansions,
      });

      await handleApiInteractionSuccess(config, {
        bag: removeProductResult.data.bag,
        sessionToken: removeProductResult.bagSessionToken,
      });

      return removeProductResult.data.bag;
    } catch (error: unknown) {
      return await handleApiInteractionError(config, error);
    }
  }) as PublicRemoveProductMethod;
